"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import FooterDisclaimer from "@/app/components/FooterDisclaimer";

const NEWSLETTER_URL = "https://investmentbastion.substack.com";

export default function Footer() {
  const pathname = usePathname();
  const [marginClass, setMarginClass] = useState("mt-[28rem] md:mt-[36rem]");

  useEffect(() => {
    // Landing served at / and /about (middleware rewrites to /landing)
    const isLandingPage = pathname === "/" || pathname === "/about" || pathname === "/landing";
    
    if (isLandingPage) {
      setMarginClass("mt-0");
    } else {
      setMarginClass("mt-[28rem] md:mt-[36rem]");
    }
  }, [pathname]);

  const currentYear = new Date().getFullYear();

  return (
    <footer className={`pt-4 md:pt-6 pb-12 md:pb-16 relative overflow-hidden ${marginClass} z-10`} suppressHydrationWarning>
      {/* Decorative divider */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />
      
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/2 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div className="group">
            <h3 className="text-xl font-semibold mb-4 text-white/90 group-hover:text-white transition-colors">
              <span className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
                Investment Bastion
              </span>
            </h3>
            <p className="text-sm text-white/65 group-hover:text-white/80 transition-colors">
              Precision tools for better investment decisions.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-semibold mb-4 text-white/80 uppercase tracking-wider">
              Navigation
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="/stocks/browse"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Browse stocks
                </a>
              </li>
              <li>
                <a
                  href="/hub"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-flex items-center gap-1 group/link cursor-pointer"
                >
                  <span>Terminal</span>
                  <svg
                    className="w-3 h-3 opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-1 transition-all duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href={NEWSLETTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-flex items-center gap-1 group/link cursor-pointer"
                >
                  <span>Newsletter</span>
                  <svg
                    className="w-3 h-3 opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-1 transition-all duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold mb-4 text-white/80 uppercase tracking-wider">
              Legal
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="/terms"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="/refund"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Refund Policy
                </a>
              </li>
              <li>
                <a
                  href="/privacy"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/cookie-policy"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Cookie Policy
                </a>
              </li>
              <li>
                <a
                  href="/risk-disclaimer"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Risk Disclaimer
                </a>
              </li>
              <li>
                <a
                  href="/complaints"
                  className="text-sm text-white/65 hover:text-white/90 transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                >
                  Complaints Policy
                </a>
              </li>
            </ul>
          </div>
        </div>

        <FooterDisclaimer currentYear={currentYear} />
      </div>
    </footer>
  );
}

